import { ACTION_CODES, getScopedActionCode, getScopedActivityEntryCode } from '@features/entity/types/actionCodes';
import {
  activitySubmitPath,
  allocationRulesPath,
  departmentReportsDraftPath,
  financeCategoriesPath,
  invitesPath,
  kpiPath,
  tagsPath,
} from './dashboardPaths';

export interface LocalDashboardAction {
  key: string;
  label: string;
  actionCode: string;
  path: string;
}

export function buildQuickActions(
  scope: string,
  scopeId: number,
  permittedActions: string[]
): LocalDashboardAction[] {
  const actions: LocalDashboardAction[] = [];
  const permitted = new Set(permittedActions);

  const saveActivityCode = getScopedActivityEntryCode(scope, 'save');
  if (saveActivityCode && permitted.has(saveActivityCode)) {
    actions.push({
      key: 'new-activity',
      label: 'New Activity Submission',
      actionCode: saveActivityCode,
      path: activitySubmitPath(scope, scopeId),
    });
  }

  const saveReport = getScopedActionCode(scope, 'SAVE_REPORT');
  if (permitted.has(saveReport)) {
    actions.push({
      key: 'department-reports',
      label: 'Department Reports',
      actionCode: saveReport,
      path: departmentReportsDraftPath(scope, scopeId),
    });
  }

  const sendInvite = getScopedActionCode(scope, 'SEND_INVITE');
  if (permitted.has(sendInvite)) {
    actions.push({
      key: 'send-invite',
      label: 'Send Invitation',
      actionCode: sendInvite,
      path: invitesPath(scope, scopeId),
    });
  }

  return actions;
}

export function buildConfigShortcuts(
  scope: string,
  scopeId: number,
  permittedActions: string[]
): LocalDashboardAction[] {
  if (scope !== 'NATION') return [];

  const permitted = new Set(permittedActions);
  const shortcuts: LocalDashboardAction[] = [];

  if (permitted.has(ACTION_CODES.CREATE_KPI)) {
    shortcuts.push({
      key: 'kpi',
      label: 'KPI Management',
      actionCode: ACTION_CODES.CREATE_KPI,
      path: kpiPath(scopeId),
    });
  }
  if (permitted.has(ACTION_CODES.CREATE_REPORT_TAG)) {
    shortcuts.push({
      key: 'tags',
      label: 'Report Tags',
      actionCode: ACTION_CODES.CREATE_REPORT_TAG,
      path: tagsPath(scopeId),
    });
  }
  if (permitted.has(ACTION_CODES.CREATE_CHURCH_FINANCIAL_CATEGORY)) {
    shortcuts.push({
      key: 'finance-categories',
      label: 'Finance Categories',
      actionCode: ACTION_CODES.CREATE_CHURCH_FINANCIAL_CATEGORY,
      path: financeCategoriesPath(scopeId),
    });
  }
  if (permitted.has(ACTION_CODES.CREATE_FINANCE_ALLOCATION_RULE)) {
    shortcuts.push({
      key: 'allocation-rules',
      label: 'Allocation Rules',
      actionCode: ACTION_CODES.CREATE_FINANCE_ALLOCATION_RULE,
      path: allocationRulesPath(scopeId),
    });
  }

  return shortcuts;
}

/** Action codes the dashboard needs for local quick actions / config shortcuts. */
export function dashboardPermissionActionCodes(scope: string): string[] {
  const codes = [
    getScopedActionCode(scope, 'SAVE_REPORT'),
    getScopedActionCode(scope, 'SEND_INVITE'),
    getScopedActivityEntryCode(scope, 'save'),
    getScopedActivityEntryCode(scope, 'submit'),
    getScopedActivityEntryCode(scope, 'review'),
  ].filter(Boolean);

  if (scope === 'NATION') {
    codes.push(
      ACTION_CODES.CREATE_KPI,
      ACTION_CODES.CREATE_REPORT_TAG,
      ACTION_CODES.CREATE_CHURCH_FINANCIAL_CATEGORY,
      ACTION_CODES.CREATE_FINANCE_ALLOCATION_RULE
    );
  }
  return codes;
}
